import { useEffect, useRef } from 'react';
import debug from '../utilities/debug';
import { navigationRef } from './navigationRef';
import {
  buildPartnerAccessRootState,
  type PartnerAccessProjectId,
} from './partnerAccessHandoff';

interface PartnerAccessRootNavigation {
  isReady: () => boolean;
  resetRoot: (state: ReturnType<typeof buildPartnerAccessRootState>) => void;
  addListener: (event: 'state', callback: () => void) => () => void;
}

/**
 * Commit a partner plan-access transition at the navigation root.
 *
 * Account switching changes AppShell from PartnerTabNavigator to
 * MainTabNavigator. This hook lives in RootNavigator, which survives that
 * child-shell replacement. It waits for the authenticated root routes to be
 * committed, then installs Main -> Plans -> ProjectHome with resetRoot.
 *
 * `navigationRef.dispatch(CommonActions.reset(...))` is not equivalent here:
 * the container forwards dispatch to the focused child navigator. resetRoot
 * adds the root navigator's key as the action target, preventing the handoff
 * from being dropped by the outgoing partner navigator.
 */
export default function usePartnerAccessRootHandoff(
  active: boolean,
  projectId: PartnerAccessProjectId | undefined,
  navigation: PartnerAccessRootNavigation = navigationRef as PartnerAccessRootNavigation,
) {
  const committedProjectRef = useRef<string | null>(null);

  useEffect(() => {
    if (!active || projectId == null) {
      committedProjectRef.current = null;
      return undefined;
    }

    const projectKey = String(projectId);
    if (committedProjectRef.current === projectKey) return undefined;

    let disposed = false;
    let unsubscribe: (() => void) | undefined;

    const commit = (): boolean => {
      if (
        disposed
        || committedProjectRef.current === projectKey
        || !navigation.isReady()
      ) {
        return committedProjectRef.current === projectKey;
      }

      // Set before resetRoot because its state event can fire synchronously.
      committedProjectRef.current = projectKey;
      try {
        navigation.resetRoot(buildPartnerAccessRootState(projectId));
        return true;
      } catch (error) {
        committedProjectRef.current = null;
        debug.warn('partner access root handoff failed', error);
        return false;
      }
    };

    if (!commit()) {
      // Cold restoration can render this hook before NavigationContainer has
      // attached its ref. Subscribe to readiness-producing state changes rather
      // than polling on a timer; the first ready state commits exactly once.
      unsubscribe = navigation.addListener('state', () => {
        if (!commit()) return;
        unsubscribe?.();
        unsubscribe = undefined;
      });

      // Close the small ready-between-check-and-subscribe race.
      if (commit()) {
        unsubscribe();
        unsubscribe = undefined;
      }
    }

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [active, navigation, projectId]);
}
